import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { Note } from './data.service';

@Injectable({ providedIn: 'root' })
export class NotesSyncApiService {
  constructor(private readonly http: HttpClient) {}

  private toApiNoteId(id: string): string {
    const raw = String(id ?? '').trim();
    if (raw.startsWith('note:')) return raw.slice('note:'.length);
    if (raw.startsWith('template:')) return raw.slice('template:'.length);
    return raw;
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders();
    return token ? headers.set('Authorization', `Bearer ${token}`) : headers;
  }

  // ---------------------------
  // CREATE
  // ---------------------------

  async createNote(note: Note): Promise<void> {
    // Never send client-only fields
    const payload = this.sanitizeForRemote(note);

    await firstValueFrom(
      this.http.post('/api/notes', payload, { headers: this.getAuthHeaders() })
    );
  }

  // ---------------------------
  // UPDATE
  // ---------------------------

  async updateNote(note: Note): Promise<void> {
    if (!note._id || !note._rev) {
      throw new Error('updateNote requires _id and remote_rev');
    }

    const apiNoteId = this.toApiNoteId(note._id);

    const payload = this.sanitizeForRemote({
      ...note,
      _rev: note._rev,
    });

    await firstValueFrom(
      this.http.put(`/api/notes/${encodeURIComponent(apiNoteId)}`, payload, { headers: this.getAuthHeaders() })
    );
  }

  // ---------------------------
  // DELETE
  // ---------------------------

  async deleteNote(noteId: string, rev: string): Promise<void> {
    if (!rev) {
      throw new Error('deleteNote requires _rev');
    }

    const apiNoteId = this.toApiNoteId(noteId);

    await firstValueFrom(
      this.http.delete(
        `/api/notes/${encodeURIComponent(apiNoteId)}`,
        { params: { rev }, headers: this.getAuthHeaders() }
      )
    );
  }



  // ---------------------------
  // HELPERS
  // ---------------------------

  /**
   * Remove all client-only / derived fields before sending to server.
   * This MUST stay in sync with NOTE_WRITABLE_FIELDS on the backend.
   */
  private sanitizeForRemote(note: Partial<Note>): Partial<Note> {
    const {
      syncStatus,
      syncTime,
      remote_rev,
      ProjectName,
      // keep _id and _rev if present
      ...rest
    } = note as any;

    // Enforce backend expectations
    if (!rest.NoteType) {
      rest.NoteType = 'To Do';
    }

    return rest;
  }

  // ---------------------------
  // CHANGES (already used)
  // ---------------------------

  async getChanges(
    since: string
  ): Promise<{ results: any[]; last_seq: string }> {
    return firstValueFrom(
      this.http.get<{ results: any[]; last_seq: string }>(
        '/api/notes/changes',
        { params: { since }, headers: this.getAuthHeaders() }
      )
    );
  }

  async bulkGetNotes(
  docs: { id: string; rev: string }[]
): Promise<any[]> {
  const res = await this.http
    .post<any>('/api/notes/bulk_get', { docs }, { headers: this.getAuthHeaders() })
    .toPromise();

  // Expecting Smorest-style response:
  // { status: "success", data: [...] }
  return res?.data ?? [];
}

}
